import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { GetNextSailingDate } from 'Services/Api/_HumanWritten/BookingWizardDataService';

export interface NextSailingParams {
	fromLocationId: string;
	toLocationId: string;
	/** The last day the rail is showing. The search starts the day after it. */
	afterDate: Date;
	isDeparture: boolean;
	/**
	 * Only vessels that carry vehicles — the vehicle tab drops every other sailing from its list, so
	 * naming one would land the booker on another empty day.
	 */
	vehiclesOnly: boolean;
	/**
	 * Only when the days on screen have nothing later to offer — a booker looking at a day with no
	 * sailings and no later day in view that has one. Everything else is already on screen.
	 */
	enabled: boolean;
}

/**
 * When the leg next departs after the days the rail is showing.
 *
 * <p>The rail knows about five days, so "the next departure" it could name was the next one inside
 * those five — and on a route that runs weekly, or seasonally, or that has simply been stepped past
 * its last sailing, there is no such day and the booker was told only that this one does not run.
 * That is the point at which they close the tab.</p>
 *
 * <p>Asked of the server's next-sailing lookup, which looks a year ahead and reads only trip stops.
 * This used to read the first sailing off eight weeks of the tickets tab's own priced data, so a
 * seasonal route whose next sailing was months away was told it had none.</p>
 *
 * <p>`undefined` while it loads, `null` when a year holds nothing. The two are different sentences
 * and the caller writes both.</p>
 */
export function useNextSailingQuery(params: NextSailingParams): {
	date: Date | null | undefined;
	isLoading: boolean;
} {
	const from = dayjs(params.afterDate).add(1, 'day').startOf('day');

	const query = useQuery({
		queryKey: [
			'trips',
			'next-sailing',
			params.fromLocationId,
			params.toLocationId,
			from.format('YYYY-MM-DD'),
			params.isDeparture,
			params.vehiclesOnly,
		],
		queryFn: ({ signal }) =>
			GetNextSailingDate(
				signal,
				params.fromLocationId,
				params.toLocationId,
				from.toDate(),
				params.isDeparture,
				params.vehiclesOnly,
			),
		enabled: params.enabled && !!params.fromLocationId && !!params.toLocationId,
		// The next sailing on a route does not move minute to minute, and a booker stepping back and
		// forth over an empty stretch must not re-ask on every step.
		staleTime: 5 * 60_000,
	});

	return { date: query.data, isLoading: query.isFetching && query.data === undefined };
}
